import {AfterViewInit, Component, ElementRef, ViewChild, ViewEncapsulation} from '@angular/core';
import {NavService} from './core/services/nav.service';
import {LogEvent} from './shared/models/models';
import {NGXLogger, NgxLoggerLevel} from "ngx-logger";
import {NavItem} from "./core/models/nav-item";
import {OktaAuthStateService} from '@okta/okta-angular';
import {OktaAuth} from '@okta/okta-auth-js';
import {AuthService} from "./core/auth/auth.service";
import {Permissions} from "./core/auth/permissions";
import {FeatureFlags, Features} from "./core/services/feature-flags.service";
import {BranchControllerService} from "falcon-api";
import {FacadeService} from "./shared/services/facade.service";
import {datadogRum} from '@datadog/browser-rum'
import {environment} from "../environments/environment";
import { OKTA_ACCESS_TOKEN_KEY, OKTA_EXPIRED_EVENT } from './shared/constants';
import {filter} from "rxjs";
import {NavigationStart, Router} from "@angular/router";
import {NaoService} from "./shared/services/nao.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements AfterViewInit  {
  title = 'onboarding-admin-portal';
  @ViewChild('appDrawer') appDrawer: ElementRef | undefined;
  isAuthenticated = false;
  userName: string = '';
  abbreviation: string = '';

  allowApplicationViewAll?: boolean;
  allowConfigurationMenuOption?: boolean;
  allowAuditLogsView?: boolean;
  allowReportLinksView?: boolean;
  allowCustomerSearch?: boolean;

  accessibleNavItems: NavItem[] = [];
  navItems: NavItem[] = [];

  /**
   * Constructor
   * @param navService Navigation service instance
   * @param logger Logger instance
   * @param authStateService Authorization service
   * @param oktaAuth Okta Authorization
   */
  constructor(private navService : NavService,
              private logger: NGXLogger,
              public authStateService: OktaAuthStateService,
              private oktaAuth: OktaAuth,
              private authService : AuthService,
              private featureFlagService: FeatureFlags,
              private falconBranchService: BranchControllerService,
              private facadeService: FacadeService,
              private naoService: NaoService,
              private router: Router) {
    // Subscribe to authentication state changes
    this.oktaAuth.authStateManager.subscribe(
      (isAuthenticated: boolean) => {
        this.isAuthenticated = isAuthenticated;
        if (this.isAuthenticated) {
          this.oktaAuth.getUser().then(userClaims => {
            this.userName = userClaims.name as string;
            this.authService.userClaims = userClaims;
            this.abbreviation = userClaims.family_name?.charAt(0)! + userClaims.given_name?.charAt(0)!;
          });

          this.checkRoleAndAssignPermissions();
          this.buildNavItems();
        }
      }
    );

    this.oktaAuth.tokenManager.on(OKTA_EXPIRED_EVENT, (key: any) => {
      if (key === OKTA_ACCESS_TOKEN_KEY) {
        console.log(`Event '${OKTA_EXPIRED_EVENT}': ${key}`);

        if (!confirm('Session is expired. Would you like to continue?')) {
          this.oktaAuth.signOut();
        }

        console.log('confirm' in window);
      }
    });

    router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe( event => {
      const url =(event as NavigationStart).url;

      naoService.isCreateBeneficiaryRoute$.next(url.substring(url.indexOf('/') + 1).includes("beneficiary"))
    });
  }

  /**
   * Called on initialization
   */
  async ngOnInit() {

    this.isAuthenticated = await this.oktaAuth.isAuthenticated();
    if (this.isAuthenticated) {
      const userClaims = await this.oktaAuth.getUser();
      this.authService.userClaims = userClaims;
      this.userName = userClaims.name as string;

      this.abbreviation = userClaims.family_name?.charAt(0)! + userClaims.given_name?.charAt(0)!;

      this.checkRoleAndAssignPermissions();
      this.buildNavItems();
    }

    if (!this.featureFlagService.isFeatureEnabled(Features.AuditLog)) {
      const logsIndex = this.navItems.findIndex(item => item.displayName === "Logs");
      this.navItems[logsIndex].children?.splice(0, 1);
    }
    this.dataDogInit();
  }

  ngOnDestroy(): void {
    datadogRum.stopSessionReplayRecording();
  }
  dataDogInit() : void {
    datadogRum.init({
      applicationId: environment.dataDogApplicationId,
      clientToken: environment.dataDogClientToken,
      site: 'datadoghq.com',
      service: environment.dataDogService,
      env: environment.env,
      sampleRate: 100,
      sessionReplaySampleRate: 10,
      trackResources: true,
      trackLongTasks: true,
      trackInteractions: true,
    });
    datadogRum.startSessionReplayRecording();
  }

  /**
   * Called after the view calls Init
   */
  ngAfterViewInit() {
    this.navService.appDrawer = this.appDrawer;
  }

  login() {
    this.oktaAuth.signInWithRedirect({
      originalUri: '/login'
    });
  }

  /**
   * Updates the logger config to the new log level
   * @param newLevel
   */
  handleLogLevelChange(newLevel: NgxLoggerLevel) {
    const updatedConfig = this.logger.getConfigSnapshot();
    updatedConfig.level = newLevel;
    updatedConfig.serverLogLevel = newLevel;
    this.logger.updateConfig(updatedConfig);
  }

  /**
   * Logs the user input using NGXLogger
   * @param log
   */
  handleLog(log: LogEvent) {
    switch (log.logType) {
      case NgxLoggerLevel.TRACE:
        this.logger.trace(log.logMessage);
        break;
      case NgxLoggerLevel.DEBUG:
        this.logger.debug(log.logMessage);
        break;
      case NgxLoggerLevel.INFO:
        this.logger.info(log.logMessage);
        break;
      case NgxLoggerLevel.LOG:
        this.logger.log(log.logMessage);
        break;
      case NgxLoggerLevel.WARN:
        this.logger.warn(log.logMessage);
        break;
      case NgxLoggerLevel.ERROR:
        this.logger.error(log.logMessage);
        break;
      case NgxLoggerLevel.FATAL:
        this.logger.fatal(log.logMessage);
        break;
    }
  }

  /**
   * Handle disable file details
   * @param disableFileDetails True if should be disabled.
   */
  handleDisableFileDetails(disableFileDetails: boolean) {
    const updatedConfig = this.logger.getConfigSnapshot();
    updatedConfig.disableFileDetails = disableFileDetails;
    this.logger.updateConfig(updatedConfig);
  }

  /**
   * Turn on or off sending logs to server.
   * @param enabled True if logs should be sent to the server.
   */
  serverLogging(enabled: boolean) {
    const updatedConfig = this.logger.getConfigSnapshot();
    updatedConfig.serverLoggingUrl = enabled ? '/dummyURL' : undefined;
    this.logger.updateConfig(updatedConfig);
  }

  /**
   * Logout
   */
  async logout() {
    await this.oktaAuth.signOut();
  }

  private checkRoleAndAssignPermissions() : void {
    const applicationListKey = Permissions.Type.VIEW_APPLICATION_LIST;

    this.authService.getAccessRole();
    this.authService.role.subscribe(role => {
      let accessPermissionList : string[] = this.authService.getAccessPermissions(role);

      this.allowApplicationViewAll = accessPermissionList.includes(applicationListKey);
      this.allowConfigurationMenuOption = accessPermissionList.includes(Permissions.Type.VIEW_CONFIGURATION);
      this.allowAuditLogsView = accessPermissionList.includes(Permissions.Type.VIEW_AUDIT_LOGS);
      this.allowReportLinksView = accessPermissionList.includes(Permissions.Type.VIEW_REPORT_LINKS);
      this.allowCustomerSearch = accessPermissionList.includes(Permissions.Type.NAO_CUSTOMER_SEARCH);

      this.buildNavItems();
    });
  }

  private buildNavItems() {
    this.navItems = [
      {
        displayName: 'Applications',
        route: 'applications',
        selected: true,
        disabled: !this.allowApplicationViewAll
      },
      {
        displayName: 'Configuration',
        route: 'configuration',
        selected: false,
        disabled: !this.allowConfigurationMenuOption
      },
      {
        displayName: 'Logs',
        route: 'logs',
        selected: false,
        disabled: !this.allowAuditLogsView,
        children: [
          {
            displayName: 'Audit',
            route: 'logs/audit',
            selected: false,
          },
          {
            displayName: 'System',
            route: 'logs/system',
            selected: false,
          },
        ]
      },
      {
        displayName: 'Reports',
        route: 'reports',
        selected: false,
        disabled: !this.allowReportLinksView,
      },
    ];

    if(this.featureFlagService.isFeatureEnabled(Features.Campaign)) {
      this.navItems.splice(2, 0,
      {
        displayName: 'Campaigns',
          route: 'campaigns',
        selected: false
        }
      );
    }
    this.accessibleNavItems = this.navItems.filter(item => !item.disabled);
  }
}
